
import { useCallback, useRef } from 'react';
import type { PermissionsState } from '@/types/permissions';
import type { Session } from '@supabase/supabase-js';
import { permissionsCircuitBreaker } from '@/utils/circuitBreaker';
import { wasRecentlyInvalidated } from '@/services/permissions';
import { cooldownUtils } from '../utils/cooldownUtils';
import { subscriptionUtils } from '../utils/subscriptionUtils';
import { useCacheHandling } from './useCacheHandling';
import { usePermissionsAPI } from './usePermissionsAPI';

interface UsePermissionsRefreshProps {
  authReady: boolean;
  userId: string | undefined;
  session: Session | null;
  setPermissions: (permissions: PermissionsState) => void;
  setSubscriptionSource: (source: 'stripe' | 'admin_override' | 'fallback' | undefined) => void;
  setIsRefreshing: (refreshing: boolean) => void;
  setError: (error: string | undefined) => void;
  isRefreshingRef: React.MutableRefObject<boolean>;
  lastRefreshedRef: React.MutableRefObject<number>;
  hasInitiallyRefreshedRef: React.MutableRefObject<boolean>;
}

/**
 * Hook for managing permissions refresh operations
 */
export const usePermissionsRefresh = ({
  authReady,
  userId,
  session,
  setPermissions,
  setSubscriptionSource,
  setIsRefreshing,
  setError,
  isRefreshingRef,
  lastRefreshedRef,
  hasInitiallyRefreshedRef,
}: UsePermissionsRefreshProps) => {
  const { tryGetCachedPermissions, cachePermissions } = useCacheHandling(
    userId,
    setPermissions,
    setSubscriptionSource,
    hasInitiallyRefreshedRef
  );

  // Create a ref to hold the refresh function for the subscription sync callback
  const refreshPermissionsRef = useRef<(() => Promise<void>) | null>(null);

  // Set up permissions API with subscription sync callback using the ref
  const { fetchFreshPermissions, forceRefreshPermissions } = usePermissionsAPI(session, async () => {
    if (refreshPermissionsRef.current) {
      await refreshPermissionsRef.current();
    }
  });

  const refreshPermissions = useCallback(async (): Promise<void> => {
    const now = Date.now();

    console.log('🔍 Permissions Refresh: Starting refresh with conditions:', {
      authReady,
      userId: !!userId,
      isRefreshing: isRefreshingRef.current
    });

    if (!authReady || !userId || isRefreshingRef.current) {
      console.log('❌ Permissions Refresh: Early return due to conditions:', {
        authReady,
        userId: !!userId,
        isRefreshing: isRefreshingRef.current
      });
      return;
    }

    // Check if cache was recently invalidated (skip cooldown in this case)
    const recentlyInvalidated = wasRecentlyInvalidated(userId);

    console.log('🔍 Permissions Refresh: Cooldown check:', {
      recentlyInvalidated,
      lastRefreshed: lastRefreshedRef.current,
      now,
      timeSinceLastRefresh: now - (lastRefreshedRef.current || 0)
    });

    // Cooldown check (unless recently invalidated)
    if (cooldownUtils.isInCooldown(lastRefreshedRef.current, recentlyInvalidated)) {
      console.log('❌ Permissions Refresh: Skipping due to cooldown');
      cooldownUtils.logCooldownSkip();
      return;
    }

    console.log('✅ Permissions Refresh: Proceeding with refresh (passed all checks)');

    // Try cache first (unless recently invalidated)
    // Pass refresh callback for lazy refresh on cache miss
    if (!recentlyInvalidated && tryGetCachedPermissions(refreshPermissions)) {
      return;
    }

    // Circuit breaker check
    if (permissionsCircuitBreaker.isOpen()) {
      setError('Service temporarily unavailable');
      return;
    }

    isRefreshingRef.current = true;
    setIsRefreshing(true);
    setError(undefined);

    try {
      const freshPermissions = await fetchFreshPermissions(userId);
      console.log('🔍 Permissions Refresh: Raw API response:', freshPermissions);

      const validatedPermissions = subscriptionUtils.checkSubscriptionExpiry(freshPermissions);
      console.log('🔍 Permissions Refresh: Validated permissions:', validatedPermissions);

      console.log('🔄 Permissions Refresh: Setting permissions state:', {
        isPremium: validatedPermissions.isPremium,
        isAdmin: validatedPermissions.isAdmin,
        source: validatedPermissions.source
      });

      setPermissions(validatedPermissions);
      setSubscriptionSource(validatedPermissions.source as 'stripe' | 'admin_override' | 'fallback');
      lastRefreshedRef.current = now;

      console.log('✅ Permissions Refresh: State update completed');

      // Cache the fresh permissions
      cachePermissions(validatedPermissions, {
        source: 'api',
        eventTimestamp: now
      });

      // Mark as initially refreshed
      hasInitiallyRefreshedRef.current = true;



    } catch (err) {
      console.error('❌ Failed to refresh permissions:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh permissions';
      setError(errorMessage);
      
      // Fallback to basic authenticated state
      const fallbackPermissions: PermissionsState = {
        isAuthenticated: true,
        isPremium: false,
        isAdmin: false,
        permissionsStatus: 'ready',
        lastChecked: now
      };
      
      setPermissions(fallbackPermissions);
      
      // Cache fallback permissions with same TTL as normal permissions
      cachePermissions(fallbackPermissions, {
        // Use default TTL (30 days) - if user has valid session, fallback should last as long as normal permissions
        source: 'fallback'
      });
      
      // Mark as initially refreshed even for fallback
      hasInitiallyRefreshedRef.current = true;
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, [
    authReady,
    userId,
    isRefreshingRef,
    lastRefreshedRef,
    hasInitiallyRefreshedRef,
    tryGetCachedPermissions,
    fetchFreshPermissions,
    cachePermissions,
    setPermissions,
    setSubscriptionSource,
    setIsRefreshing,
    setError,
  ]);

  // Update the ref with the current refresh function
  refreshPermissionsRef.current = refreshPermissions;

  return { refreshPermissions, forceRefreshPermissions };
};
